import { brandsLookup } from '@zealot/shared/apiClient';
import { brandLabelFromHost } from '@zealot/shared/brands/brandLabelFromHost';
import Router from 'next/router';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { getHubBrand, getStoredBrandTheme, useAppStore } from '@/store';
import { addressBrandSources } from '@/store/slices/brandScope';
import type { HubBrandSlice } from '@/store/slices/hubBrand';
import { applyBrandLinkColor } from './applyBrandLinkColor';
import { buildBrandLookupQuery } from './buildBrandLookupQuery';
import { brandSourcesByName } from './sources/brandSourcesByName';
import { type ResolvedBrandIdentifiers, resolveBrandCandidates } from './sources/resolveBrand';
import type { BrandIdentifiers } from './sources/types';

type BrandConfig = {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  icon?: string | null;
  colorBrandLogo?: string | null;
  whiteBrandLogo?: string | null;
  loaderGIFURL?: string | null;
  loginBackground?: string | null;
  programTitle?: string | null;
  brandTitle?: string | null;
  favicon?: string | null;
  welcomeImage?: string | null;
  onboardingOverviewCopy?: string | null;
  skipOnboardingOverview?: boolean | null;
  linkColor?: string | null;
  [key: string]: unknown;
};

type BrandData = {
  uuid?: string;
  brandID?: number | string;
  code?: string;
  brandName?: string;
  brandConfig?: BrandConfig | null;
  [key: string]: unknown;
};

type PartialBrandData = Partial<BrandData> & {
  brandConfig?: Partial<BrandConfig>;
};

type BrandContextValue = {
  brand: BrandData | null;
  isLoading: boolean;
  // why: isLoading tracks theme readiness only - a stored theme counts as complete while the lookup carrying the
  // why: rest of brandConfig (e.g. defaultCompletionRedirectUrl) is still in flight. True once a lookup has settled.
  isConfigSettled: boolean;
  error: string | null;
  setBrandData: (data: PartialBrandData) => void;
};

const BrandContext = createContext<BrandContextValue | null>(null);

const isBrandComplete = (brand: BrandData | PartialBrandData | null): brand is BrandData => {
  if (!brand) return false;
  return !!(brand.uuid && brand.code && brand.brandName && brand.brandConfig?.primaryColor && brand.brandConfig?.icon);
};

// gloss
const brandAnswersRequest = (
  brand: BrandData | null,
  identifiers: { uuid?: string; brandID?: string; code?: string },
) => {
  if (!brand) return false;
  if (identifiers.uuid) return brand.uuid === identifiers.uuid;
  if (identifiers.brandID) return String(brand.brandID ?? '') === identifiers.brandID;
  if (identifiers.code) {
    const code = identifiers.code.toLowerCase();
    return brand.code?.toLowerCase() === code || brand.brandName?.toLowerCase() === code;
  }
  return false;
};

// gloss
const candidateKey = (candidate: BrandIdentifiers): string =>
  `${candidate.uuid ?? ''}|${candidate.brandID ?? ''}|${candidate.code ?? ''}`;

// gloss
const hostLabelFor = (
  nextUuid: string | undefined,
  resolvedBySource: ResolvedBrandIdentifiers['source'] | null,
): string | undefined => {
  if (resolvedBySource === 'url') return brandLabelFromHost(window.location.hostname) ?? undefined;

  const hub = getHubBrand();

  return Boolean(nextUuid) && nextUuid === hub.uuid ? hub.hubLabel : undefined;
};

// gloss
const publishedHubIsStillAsked = (
  candidates: readonly ResolvedBrandIdentifiers[],
  hub: HubBrandSlice['hubBrand'] = getHubBrand(),
): boolean => {
  if (!hub.uuid && !hub.brandID && !hub.code && !hub.brandName) return true;
  if (candidates.length === 0) return false;

  return candidates.some((candidate) => brandAnswersRequest(hub as BrandData, candidate));
};

const brandMatchesPartial = (existing: BrandData | null, partial: PartialBrandData): boolean => {
  if (!existing) return false;

  if (partial.uuid && existing.uuid !== partial.uuid) return false;
  if (partial.code && existing.code !== partial.code) return false;
  if (partial.brandName && existing.brandName !== partial.brandName) return false;

  return true;
};

// gloss
const brandFromStore = (hub: HubBrandSlice['hubBrand'] = getHubBrand()): PartialBrandData | null => {
  if (!hub.uuid && !hub.code && !hub.brandName) return null;

  const theme = getStoredBrandTheme();

  return {
    uuid: hub.uuid,
    brandID: hub.brandID,
    code: hub.code,
    brandName: hub.brandName,
    brandConfig: {
      colorBrandLogo: theme.colorBrandLogo || theme.icon,
      whiteBrandLogo: theme.whiteBrandLogo,
      icon: theme.icon,
      loaderGIFURL: theme.loaderGIFURL,
      primaryColor: theme.primaryColor,
      loginBackground: theme.loginBackground,
      linkColor: theme.linkColor,
      programTitle: theme.programTitle,
      welcomeImage: theme.welcomeImage,
    } as PartialBrandData['brandConfig'],
  };
};

// gloss
const getInitialBrandState = (): { brand: BrandData | null; isComplete: boolean } => {
  if (typeof window === 'undefined') {
    return { brand: null, isComplete: false };
  }

  const hub = getHubBrand();
  const storedBrand = brandFromStore(hub);
  // why: the store holds the last hub this browser visited; when the address names a different hub, painting it shows the wrong brand until the lookup lands (ZLT-6041), so wait for the lookup instead.
  // why: first paint runs before BrandScope records the page's sources, so this reads the default hub scope.
  const addressSources = addressBrandSources(useAppStore.getState().brandScope.sources);
  const addressAsks = resolveBrandCandidates(brandSourcesByName(addressSources), {
    url: new URL(window.location.href),
    hostname: window.location.hostname,
  });
  if (addressAsks.length > 0 && !publishedHubIsStillAsked(addressAsks, hub)) {
    return { brand: null, isComplete: false };
  }
  if (isBrandComplete(storedBrand)) {
    return { brand: storedBrand, isComplete: true };
  }

  return { brand: storedBrand as BrandData | null, isComplete: false };
};

// gloss
export const BrandProvider = ({ children }: { children: ReactNode }) => {
  const client = useApiClient();
  const [routeKey, setRouteKey] = useState('');

  const initialState = getInitialBrandState();
  const [brand, setBrand] = useState<BrandData | null>(initialState.brand);
  const [isLoading, setIsLoading] = useState(!initialState.isComplete);
  const [isConfigSettled, setIsConfigSettled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchTriggeredRef = useRef<string | null>(null);
  const resolvedBySourceRef = useRef<ResolvedBrandIdentifiers['source'] | null>(null);
  const candidatesRef = useRef<ResolvedBrandIdentifiers[]>([]);
  const missedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const onRouteChange = (url: string) => setRouteKey(url);
    Router.events?.on?.('routeChangeComplete', onRouteChange);
    return () => {
      Router.events?.off?.('routeChangeComplete', onRouteChange);
    };
  }, []);

  const setHubBrand = useAppStore((s) => s.hubBrand.setHubBrand);
  const markHubBrandResolved = useAppStore((s) => s.hubBrand.markResolved);
  const markHubBrandSwitching = useAppStore((s) => s.hubBrand.markSwitching);
  const setBrandTheme = useAppStore((s) => s.brandTheme.setBrandTheme);
  const resolveHubToNothing = useAppStore((s) => s.hubBrand.resolveToNothing);
  useEffect(() => {
    if (!brand) {
      if (isLoading) return;
      if (publishedHubIsStillAsked(candidatesRef.current)) markHubBrandResolved();
      else resolveHubToNothing();
      return;
    }
    const nextUuid = (brand as BrandData & { brandUuid?: string }).brandUuid ?? brand.uuid;
    setHubBrand({
      uuid: nextUuid,
      brandID: brand.brandID === undefined || brand.brandID === null ? undefined : String(brand.brandID),
      code: brand.code,
      brandName: brand.brandName,
      hubLabel: hostLabelFor(nextUuid, resolvedBySourceRef.current),
    });
    if (brand.brandConfig) setBrandTheme(brand.brandConfig);
  }, [brand, isLoading, setHubBrand, markHubBrandResolved, resolveHubToNothing, setBrandTheme]);

  useEffect(() => {
    applyBrandLinkColor(brand?.brandConfig?.linkColor);
  }, [brand?.brandConfig?.linkColor]);

  const fetchBrand = useCallback(
    async (candidates: ResolvedBrandIdentifiers[]) => {
      const askable = candidates.filter((c) => c.uuid || c.brandID || c.code);
      if (askable.length === 0) {
        setIsLoading(false);
        setIsConfigSettled(true);
        return;
      }

      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      setIsConfigSettled(false);

      try {
        for (const identifiers of askable) {
          const query = buildBrandLookupQuery(identifiers);

          const response = await brandsLookup({ client, query });

          if (abortController.signal.aborted) return;

          if (response.data?.data) {
            resolvedBySourceRef.current = identifiers.source;
            setBrand(response.data.data as BrandData);
            return;
          }

          const http = response.response as Response | undefined;
          if (http && !http.ok && http.status !== 404) {
            setError(`Brand lookup failed (${http.status})`);
            return;
          }

          missedRef.current.add(candidateKey(identifiers));
        }
      } catch (e) {
        if (abortController.signal.aborted) return;
        setError(e instanceof Error ? e.message : 'Failed to fetch brand');
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
          setIsConfigSettled(true);
        }
      }
    },
    [client],
  );

  const setBrandData = useCallback(
    (partial: PartialBrandData) => {
      setBrand((existing: BrandData | null) => {
        if (isBrandComplete(existing) && brandMatchesPartial(existing, partial)) {
          setIsLoading(false);
          return existing;
        }

        // why: a partial naming a different brand must not inherit the cached brand's colors and
        // why: logo, or the merge reads as complete and the old brand is shown under the new uuid.
        const base = brandMatchesPartial(existing, partial) ? existing : null;
        const merged: PartialBrandData = {
          ...base,
          ...partial,
          brandConfig: {
            ...base?.brandConfig,
            ...partial.brandConfig,
          },
        };

        if (isBrandComplete(merged)) {
          setIsLoading(false);
          return merged;
        }

        const identifiers: ResolvedBrandIdentifiers = {
          uuid: partial.uuid || base?.uuid,
          brandID: String(partial.brandID || base?.brandID || ''),
          code: partial.code || base?.code,
          source: 'none',
        };
        resolvedBySourceRef.current = null;
        fetchBrand([identifiers]);

        return merged as BrandData;
      });
    },
    [fetchBrand],
  );

  const brandScope = useAppStore((s) => s.brandScope);
  useEffect(() => {
    const candidates = resolveBrandCandidates(brandSourcesByName(brandScope.sources), {
      url: new URL(window.location.href),
      hostname: window.location.hostname,
      resource: brandScope.resource,
    });

    candidatesRef.current = candidates;
    const requestedKey = candidates.map(candidateKey).join('>');
    if (fetchTriggeredRef.current === requestedKey) {
      return;
    }
    fetchTriggeredRef.current = requestedKey;

    if (candidates.length === 0) {
      abortControllerRef.current?.abort();
      setBrand(null);
      setIsLoading(false);
      setIsConfigSettled(true);
      return;
    }

    const stillAnswering = candidates.find((c) => !missedRef.current.has(candidateKey(c))) ?? candidates[0]!;
    const answersRequest = brandAnswersRequest(brand, stillAnswering);

    if (brand && isBrandComplete(brand) && answersRequest) {
      setIsLoading(false);
      fetchBrand(candidates);
      return;
    }

    // why: first paint skips a stored hub the address does not name (ZLT-6041), so brand can be null while the store still holds that hub.
    if (!answersRequest && (brand || brandFromStore())) {
      setBrand(null);
      setIsLoading(true);
      markHubBrandSwitching();
    }

    fetchBrand(candidates);
  }, [fetchBrand, brand, client, routeKey, brandScope, markHubBrandSwitching]);

  // why: keep this memoized value stable — an unstable brand context value feeds a
  // why: render-setState loop with ApiClientContext (React #185).
  const value = useMemo(
    () => ({ brand, isLoading, isConfigSettled, error, setBrandData }),
    [brand, isLoading, isConfigSettled, error, setBrandData],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) throw new Error('useBrand must be used within BrandProvider');
  return context;
};

export { BrandContext };
